/**
 * Configurable "space" noun (migration 0097) — shared pure helpers.
 *
 * Kennels say "run"; catteries say "pen" / "chalet" / "suite"; US operators
 * "room". Every user-visible "Run"/"Runs" string in the staff app and the
 * customer portal reads through `useSpaceNoun()` (staff: `@/lib/useSpaceNoun`,
 * customer: `customer/lib/useSpaceNoun`) which both resolve via this module.
 *
 * Code identifiers, routes and the `runs` table keep their names — only copy
 * changes.
 */

export interface SpaceNounPair {
  singular: string;
  plural: string;
}

export interface SpaceNounForms extends SpaceNounPair {
  /** Capitalised singular, e.g. "Pen". */
  Singular: string;
  /** Capitalised plural, e.g. "Pens". */
  Plural: string;
}

export const DEFAULT_SPACE_NOUN: SpaceNounPair = { singular: 'run', plural: 'runs' };

/** Preset choices in /settings/account. Mirrors the API's SPACE_NOUN_PRESETS. */
export const SPACE_NOUN_PRESETS: ReadonlyArray<SpaceNounPair & { label: string }> = [
  { label: 'Run', singular: 'run', plural: 'runs' },
  { label: 'Pen', singular: 'pen', plural: 'pens' },
  { label: 'Chalet', singular: 'chalet', plural: 'chalets' },
  { label: 'Suite', singular: 'suite', plural: 'suites' },
  { label: 'Room', singular: 'room', plural: 'rooms' },
  { label: 'Kennel', singular: 'kennel', plural: 'kennels' },
];

export function capitalise(word: string): string {
  if (word.length === 0) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Resolve the four forms from whatever the server gave us. Accepts either
 * the already-resolved pair (`/api/me.spaceNoun`, `/api/customer/branding
 * .spaceNoun`) or the raw nullable account columns; both fall back to the
 * platform default. Singular-only input derives the plural as `<singular>s`.
 */
export interface SpaceNounSource {
  singular?: string | null;
  plural?: string | null;
  spaceNounSingular?: string | null;
  spaceNounPlural?: string | null;
}

export function resolveSpaceNounForms(input: SpaceNounSource | null | undefined): SpaceNounForms {
  const singular = (input?.singular ?? input?.spaceNounSingular)?.trim() || null;
  const plural = (input?.plural ?? input?.spaceNounPlural)?.trim() || null;
  const s = singular ?? DEFAULT_SPACE_NOUN.singular;
  const p = plural ?? (singular ? `${singular}s` : DEFAULT_SPACE_NOUN.plural);
  return { singular: s, plural: p, Singular: capitalise(s), Plural: capitalise(p) };
}

/** Pick the preset matching a pair, or null when it's a custom noun. */
export function matchSpaceNounPreset(
  pair: SpaceNounPair,
): (typeof SPACE_NOUN_PRESETS)[number] | null {
  const s = pair.singular.trim().toLowerCase();
  const p = pair.plural.trim().toLowerCase();
  return SPACE_NOUN_PRESETS.find((x) => x.singular === s && x.plural === p) ?? null;
}
